import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StaffShell } from '../../components/StaffShell';
import { AddPatientModal } from '../../components/AddPatientModal';
import { LabUploadHistory } from '../../components/LabUploadHistory';
import { PatientAvatar } from '../../components/PatientAvatar';
import { useUserStore } from '../../store/userStore';
import { getClinicianDashboard, getLabUploads } from '../../lib/api';

const TRIAGE_LABELS = {
  not_reviewed: { label: 'Not reviewed', color: '#94A3B8' },
  in_review: { label: 'In review', color: '#3B82F6' },
  awaiting_biomarkers: { label: 'Awaiting biomarkers', color: '#F59E0B' },
  reviewed_plan_issued: { label: 'Reviewed', color: '#10B981' },
  follow_up_due: { label: 'Follow-up due', color: '#EC4899' },
};

const STAT_CARDS = [
  { key: 'not_reviewed', label: 'Not reviewed', icon: 'fa-inbox', color: '#94A3B8' },
  { key: 'in_review', label: 'In review', icon: 'fa-hourglass-half', color: '#3B82F6' },
  { key: 'awaiting_biomarkers', label: 'Awaiting biomarkers', icon: 'fa-vial', color: '#F59E0B' },
  { key: 'reviewed', label: 'Reviewed', icon: 'fa-circle-check', color: '#10B981' },
  { key: 'follow_up_due', label: 'Follow-up due', icon: 'fa-bell', color: '#EC4899' },
];

export default function ClinicianDashboard() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [uploads, setUploads] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    getClinicianDashboard(user.id).then(setData).finally(() => setLoading(false));
    getLabUploads(user.id).then((r) => setUploads((r || []).slice(0, 15))).catch(() => {});
  }, [user?.id]);

  const patients = (data?.patients || []).filter(
    (p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.email || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <StaffShell title="Clinician" badge="Triage & Reviews">
      {adding && (
        <AddPatientModal
          clinicianId={user.id}
          onClose={() => setAdding(false)}
          onCreated={(p) => { setAdding(false); navigate(`/clinician/patient/${p.id}`); }}
        />
      )}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-white mb-1">Triage dashboard</h1>
          <p className="text-white/50">Welcome, {user?.name}. {data?.total_patients || 0} patients in your caseload.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="my-schedule-btn"
            onClick={() => navigate('/clinician/schedule')}
            className="rounded-2xl px-4 py-2.5 flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 transition-base"
          >
            <i className="fa-solid fa-calendar-days" /> My schedule
          </button>
          <button
            data-testid="add-patient-btn"
            onClick={() => setAdding(true)}
            className="aura-btn-gradient text-white font-semibold rounded-2xl px-4 py-2.5 flex items-center gap-2 transition-base"
          >
            <i className="fa-solid fa-user-plus" /> Add patient
          </button>
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {STAT_CARDS.map((s) => (
          <div key={s.key} className="glass-dark rounded-2xl p-4">
            <div className="grid place-items-center w-10 h-10 rounded-xl mb-2" style={{ background: `${s.color}22`, color: s.color }}><i className={`fa-solid ${s.icon}`} /></div>
            <div className="font-display text-2xl text-white">{data?.status_counts?.[s.key] ?? 0}</div>
            <div className="text-xs text-white/50">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="glass-dark rounded-2xl p-3 mb-4 flex items-center gap-3">
        <i className="fa-solid fa-magnifying-glass text-white/40" />
        <input data-testid="patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients…" className="flex-1 bg-transparent text-white placeholder-white/40 outline-none" />
      </div>

      {loading ? (
        <div className="glass-dark rounded-2xl p-10 text-center text-white/50"><i className="fa-solid fa-circle-notch fa-spin" /></div>
      ) : (
        <div className="glass-dark rounded-2xl overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
            <div className="col-span-4">Patient</div>
            <div className="col-span-2">Stage</div>
            <div className="col-span-3">Dominant pattern</div>
            <div className="col-span-3">Status</div>
          </div>
          {patients.map((p) => {
            const t = TRIAGE_LABELS[p.triage_status] || TRIAGE_LABELS.not_reviewed;
            return (
              <button
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                onClick={() => navigate(`/clinician/patient/${p.id}`)}
                className="w-full grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-0 px-4 py-3.5 text-left border-b border-white/5 hover:bg-white/5 transition-base sm:items-center"
              >
                <div className="col-span-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <PatientAvatar patient={p} size="sm" />
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{p.name}</div>
                      <div className="text-xs text-white/40 truncate">{p.email?.endsWith('@patients.auralis.local') ? 'No login email' : p.email}</div>
                    </div>
                  </div>
                  {/* status chip inline on mobile */}
                  <span className="sm:hidden text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ color: t.color, background: `${t.color}22` }}>{t.label}</span>
                </div>
                <div className="col-span-2 text-white/70 text-sm capitalize">
                  <span className="sm:hidden text-white/40">Stage: </span>{p.stage}
                </div>
                <div className="col-span-3 text-white/70 text-sm">
                  <span className="sm:hidden text-white/40">Pattern: </span>{p.dominant_pattern}
                </div>
                <div className="hidden sm:block col-span-3">
                  <span className="text-xs font-semibold rounded-full px-3 py-1" style={{ color: t.color, background: `${t.color}22` }}>{t.label}</span>
                </div>
              </button>
            );
          })}
          {patients.length === 0 && <div className="p-8 text-center text-white/40">No patients found.</div>}
        </div>
      )}

      <div className="glass-dark rounded-2xl p-5 mt-6" data-testid="recent-uploads-panel">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg text-white flex items-center gap-2"><i className="fa-solid fa-clock-rotate-left text-[var(--aura-purple-light)]" /> Recent lab uploads</h2>
          <span className="text-xs text-white/40">{uploads.length} shown</span>
        </div>
        <LabUploadHistory uploads={uploads} showPatient emptyText="No lab reports have been uploaded yet." />
      </div>
    </StaffShell>
  );
}
